import { Inject, Injectable, Optional, Provider, inject } from '@angular/core';
import { ApiService } from './api.service';
import { IQueryConfig, IQueryResponse, IQueryResponseWsz, QUERY_CONFIG, QueryState } from '../models/query.model';
import { SignalStore, SignalStoreConfig } from 'src/app/shared/functions/store.functions';
import { queryStateConfig } from 'src/app/config/query.config';
import { deepMerge } from 'src/app/shared/functions/object.function';
import { toObservable } from '@angular/core/rxjs-interop';
import { IPageEvent } from '../models/paginator.model';
import { ISort, ISortEvent } from '../models/sort.model';
import { ISearchEvent } from '../models/table.model';
import { IFilterEvent, IFilters } from '../models/filters.model';
import { ApiWszService } from './api-wsz.service';

@Injectable()
export class QueryWszService<T> {
  store: SignalStore<QueryState<T>>;

  constructor(
    @Inject('endpoint') private endpoint: string,
    @Optional() @Inject(QUERY_CONFIG) private readonly userConfig: SignalStoreConfig<QueryState<T>>,
    @Inject(ApiWszService) private readonly apiService: ApiWszService,
  ) {
    this.defaultConfig = deepMerge(this.defaultConfig, this.userConfig);
    this.store = new SignalStore<QueryState<T>>(this.endpoint + '-store', this.defaultConfig);
    toObservable(this.store.selectKey('sort')).subscribe(() => {
      this.setPageIndex(0);
    });
    toObservable(this.store.selectKey('search')).subscribe(() => {
      this.setPageIndex(0);
    });
    toObservable(this.store.selectKey('filters')).subscribe(() => {
      this.setPageIndex(0);
    });
  }

  private defaultConfig = queryStateConfig;

  private setPageIndex(index: number) {
    this.store.setKey('pageable', {
      ...this.store.selectKey('pageable')(),
      ...{
        page: index
      }
    });
  }

  private cleanUpFilterField(field: string) {
    let result: string = field;
    result = result.replaceAll('_', '.');
    return result;
  }

  private cleanUpFilterValue(value: string) {
    let result: string = `${value}`;
    result = result.replaceAll('T00:00:00.000+01:00', '');
    return result;
  }

  private getUrl(endpoint: string, config?: IQueryConfig) {
    let url = `${endpoint}?page=${config?.pageable?.page ?? 0}&size=${config?.pageable?.size ?? this.defaultConfig.initState?.pageable?.size}`;
    //sort
    let sort = config?.sort ?? this.defaultConfig.initState?.sort;
    if (sort) {
      url += `&sort=${sort.field},${sort.direction}`;
    }
    //search
    if (config?.search) {
      url += `&nome.LIKE=${config.search}`;
    }
    //filters
    for (const key in config?.filters) {
      if (config?.filters.hasOwnProperty(key) && config?.filters[key] !== null) {
        url += `&${this.cleanUpFilterField(key)}=${this.cleanUpFilterValue(config?.filters[key])}`;
      }
    }
    return url;
  }

  get list() {
    return this.store.selectKey('list');
  }

  get search() {
    return this.store.selectKey('search');
  }

  get filters() {
    return this.store.selectKey('filters');
  }

  get sort() {
    return this.store.selectKey('sort');
  }

  get pageable() {
    return this.store.selectKey('pageable');
  }

  onSort(sortEvent: ISortEvent) {
    let sort: ISort | undefined = sortEvent.direction !== '' ? { field: sortEvent.active, direction: sortEvent.direction } : undefined;
    this.store.setKey('sort', sort);
  }

  onSearch(searchEvent: ISearchEvent) {
    let search = searchEvent.value && searchEvent.value !== '' ? searchEvent.value : undefined;
    this.store.setKey('search', search);
  }

  onFilter(filterEvent: IFilterEvent) {
    this.store.setKey('filters', filterEvent);
  }

  onPage(pageEvent: IPageEvent) {
    this.store.setKey('pageable', {
      ...this.store.selectKey('pageable')(),
      ...{
        page: pageEvent.pageIndex,
        size: pageEvent.pageSize,
      }
    });
  }

  get(params?: IQueryConfig, callback?: () => void) {
    if (this.store.selectKey('isLoading')() === false) {
      this.store.setKey('isLoading', true);
      let url = this.getUrl(this.endpoint, params)
      this.apiService.get<IQueryResponseWsz>(url).subscribe((response) => {
        this.store.setKey('list', response.content);
        this.store.setKey('pageable', {
          page: response.page.number,
          size: response.page.size,
          totalElements: response.page.totalElements,
          totalPages: response.page.totalPages,
        });
        if (callback) {
          callback();
        }
      }).add(() => {
        this.store.setKey('isLoading', false);
      })
    }
  }

  getConcat(params?: IQueryConfig, callback?: () => void) {
    if (this.store.selectKey('isLoading')() === false) {
      this.store.setKey('isLoading', true);
      let url = this.getUrl(this.endpoint, params)
      console.log(url)
      this.apiService.get<IQueryResponseWsz>(url).subscribe((response) => {
        this.store.setKey('list',   this.store.selectKey('list')().concat(response.content));
        this.store.setKey('pageable', {
          page: response.page.number,
          size: response.page.size,
          totalElements: response.page.totalElements,
          totalPages: response.page.totalPages,
        });
        if (callback) {
          callback();
        }
      }).add(() => {
        this.store.setKey('isLoading', false);
      })
    }
  } 


  getAll(filters?: IFilters) {
    this.get({ pageable: { size: 99999 }, filters: filters });
  }
}

export function provideQueryWszService<T>(
  endpoint: string,
  userConfig?: SignalStoreConfig<QueryState<T>>,
): Provider[] {
  return [
    {
      provide: createTokenQueryServiceInstance(endpoint),
      useFactory: () => new QueryWszService<T>(endpoint, userConfig ?? queryStateConfig, inject(ApiWszService))
    }
  ]
}

export function createTokenQueryServiceInstance(endpoint: string) {
  return `${endpoint}-query-service`;
}
